/**
 * Hariom ERP - Root Application Component
 * Sets up React Router, protected routes, and global providers
 */
import React from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import { useAuthStore } from './store/authStore'
import { useLiveSync } from './hooks/useLiveSync'

// Layout
import AppLayout from './components/layout/AppLayout'

// Pages
import Login       from './pages/auth/Login'
import Dashboard   from './pages/dashboard/Dashboard'
import DemandForecast from './pages/forecast/DemandForecast'
import Purchase     from './pages/purchase/Purchase'
import SalesOrder   from './pages/salesorder/SalesOrder'
import DiamondPurchase from './pages/diamondpurchase/DiamondPurchase'
import DebtorReport from './pages/debtor/DebtorReport'
import Inventory   from './pages/inventory/Inventory'
import Production  from './pages/production/Production'
import Sales       from './pages/sales/Sales'
import QualityControl from './pages/qc/QualityControl'
import Finance     from './pages/finance/Finance'
import HR          from './pages/hr/HR'
import Reports     from './pages/reports/Reports'
import Settings    from './pages/settings/Settings'

/**
 * ProtectedRoute: Redirects to /login if user is not authenticated.
 * Wrap any route that requires login with this component.
 */
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuthStore()
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />
}

/**
 * Restricted roles and the page(s) each may use:
 *   marketing (Shoaib, Ved Prakash) → Demand Forecast only
 *   purchase  (Ashish, Amris)       → Purchase only
 *   sales     (Bhavesh)             → Sales Order + Diamond Purchase
 *   salesorder(Vinayak)             → Sales Order only
 * Any role not listed here (admin/founder) can access everything.
 */
const RESTRICTED: Record<string, string[]> = {
  marketing: ['/forecast'],
  purchase: ['/purchase'],
  sales: ['/sales-order', '/diamond-purchase'],
  salesorder: ['/sales-order'],
  accountant: ['/debtor'],
}

/** HomeRedirect: send each user to their landing page after login. */
const HomeRedirect: React.FC = () => {
  const { user } = useAuthStore()
  return <Navigate to={(user && RESTRICTED[user.role]?.[0]) || '/dashboard'} replace />
}

/**
 * Guard a page by path. A restricted role may only open its allowed page(s); if
 * it tries any other page it is redirected back to its first allowed one.
 */
const Guard: React.FC<{ path: string; children: React.ReactNode }> = ({ path, children }) => {
  const { user } = useAuthStore()
  const allowed = user ? RESTRICTED[user.role] : undefined
  if (allowed && !allowed.includes(path)) return <Navigate to={allowed[0]} replace />
  return <>{children}</>
}

const App: React.FC = () => {
  useLiveSync()   // pull latest shared data from the server across devices
  return (
    <BrowserRouter>
      {/* Global toast notifications (top-right position) */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: {
            fontSize: '14px',
            borderRadius: '10px',
            fontFamily: 'Inter, sans-serif',
          },
        }}
      />

      <Routes>
        {/* ===== PUBLIC ROUTES ===== */}
        <Route path="/login" element={<Login />} />

        {/* ===== PROTECTED ROUTES (require login) ===== */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          {/* Default redirect: role-aware landing page */}
          <Route index element={<HomeRedirect />} />

          {/* Each page is guarded so restricted roles only see their own page */}
          <Route path="forecast"   element={<Guard path="/forecast"><DemandForecast /></Guard>} />
          <Route path="purchase"   element={<Guard path="/purchase"><Purchase /></Guard>} />
          <Route path="sales-order" element={<Guard path="/sales-order"><SalesOrder /></Guard>} />
          <Route path="diamond-purchase" element={<Guard path="/diamond-purchase"><DiamondPurchase /></Guard>} />
          <Route path="debtor"     element={<Guard path="/debtor"><DebtorReport /></Guard>} />
          <Route path="dashboard"  element={<Guard path="/dashboard"><Dashboard /></Guard>} />
          <Route path="inventory"  element={<Guard path="/inventory"><Inventory /></Guard>} />
          <Route path="production" element={<Guard path="/production"><Production /></Guard>} />
          <Route path="sales"      element={<Guard path="/sales"><Sales /></Guard>} />
          <Route path="quality"    element={<Guard path="/quality"><QualityControl /></Guard>} />
          <Route path="finance"    element={<Guard path="/finance"><Finance /></Guard>} />
          <Route path="hr"         element={<Guard path="/hr"><HR /></Guard>} />
          <Route path="reports"    element={<Guard path="/reports"><Reports /></Guard>} />
          <Route path="settings"   element={<Guard path="/settings"><Settings /></Guard>} />
        </Route>

        {/* Catch-all: redirect unknown routes to dashboard */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </BrowserRouter>
  )
}

export default App
